import React, { useEffect, useState } from 'react';
import { learnerApi, adminApi } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useLocation } from 'react-router-dom';
import Spinner from '../../components/common/Spinner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, ReferenceLine, Legend,
} from 'recharts';

const gradeColor = v => v >= 75 ? '#10b981' : v >= 50 ? '#f59e0b' : '#ef4444';
const gradeSymbol = v => v >= 80 ? 'A' : v >= 70 ? 'B' : v >= 60 ? 'C' : v >= 50 ? 'D' : v >= 40 ? 'E' : 'F';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:8, padding:'10px 14px', boxShadow:'0 4px 12px rgba(0,0,0,0.1)' }}>
      <div style={{ fontWeight:600, marginBottom:4, fontSize:'0.85rem' }}>{label}</div>
      {payload.map((p,i) => (
        <div key={i} style={{ color:p.color, fontSize:'0.82rem' }}>{p.name}: {p.value}%</div>
      ))}
    </div>
  );
};

export default function LearnerMarks() {
  const { school }   = useAuth();
  const location     = useLocation();
  const primary      = school?.primary_color || '#0d6efd';

  const [data,       setData]       = useState(null);
  const [terms,      setTerms]      = useState([]);
  const [termId,     setTermId]     = useState('');
  const [subjectId,  setSubjectId]  = useState(location.state?.subjectId || '');
  const [loading,    setLoading]    = useState(true);
  const [activeTab,  setActiveTab]  = useState('overview'); // 'overview' | 'detail' | 'trends'

  useEffect(() => { adminApi.getTerms().then(r => setTerms(r.data.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    learnerApi.getMarks(termId || null, subjectId || null)
      .then(r => setData(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [termId, subjectId]);

  if (loading) return <Spinner />;
  if (!data) return <div className="sms-card" style={{ textAlign:'center', color:'#9ca3af', padding:'60px 0' }}>Failed to load marks</div>;

  const { subjects, assessments, trend, overall_avg } = data;

  // Subject comparison bar chart data
  const subjectBarData = subjects.map(s => ({
    name: s.subject_name.length > 12 ? s.subject_name.slice(0,12)+'…' : s.subject_name,
    mine: parseFloat(s.my_avg) || 0,
    class: parseFloat(s.class_avg) || 0,
  }));

  // Trend data for line chart
  const trendData = trend.map(t => ({
    month: t.month,
    avg: parseFloat(t.avg_pct),
  }));

  // Filter assessments by selected subject
  const filteredAssessments = subjectId
    ? assessments.filter(a => String(a.subject_id) === String(subjectId))
    : assessments;

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <h2>My Marks</h2>
          <p>{data.total_assessments} assessments{overall_avg ? ` · Overall average: ${overall_avg}%` : ''}</p>
        </div>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          <select className="sms-input" style={{ width:160 }} value={termId} onChange={e => setTermId(e.target.value)}>
            <option value="">All Terms</option>
            {terms.map(t => <option key={t.term_id} value={t.term_id}>{t.name} {t.year}</option>)}
          </select>
          <select className="sms-input" style={{ width:190 }} value={subjectId} onChange={e => setSubjectId(e.target.value)}>
            <option value="">All Subjects</option>
            {subjects.map(s => <option key={s.subject_id} value={s.subject_id}>{s.subject_name}</option>)}
          </select>
        </div>
      </div>

      {subjects.length === 0 ? (
        <div className="sms-card" style={{ textAlign:'center', color:'#9ca3af', padding:'60px 0' }}>No marks recorded yet</div>
      ) : (
        <>
          {/* Overall summary cards */}
          {!subjectId && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:12, marginBottom:24 }}>
              {subjects.map(s => {
                const avg = parseFloat(s.my_avg);
                const col = gradeColor(avg);
                return (
                  <button key={s.subject_id} onClick={() => setSubjectId(String(s.subject_id))}
                    style={{ background:`${col}09`, border:`2px solid ${col}33`, borderRadius:12, padding:'14px 10px',
                      cursor:'pointer', textAlign:'center', transition:'all 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = col}
                    onMouseLeave={e => e.currentTarget.style.borderColor = `${col}33`}>
                    <div style={{ fontSize:'1.6rem', fontWeight:800, color:col, lineHeight:1 }}>{gradeSymbol(avg)}</div>
                    <div style={{ fontSize:'1rem', fontWeight:700, color:col }}>{avg}%</div>
                    <div style={{ fontSize:'0.72rem', color:'#6b7280', marginTop:3, fontWeight:500,
                      whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{s.subject_name}</div>
                    <div style={{ fontSize:'0.68rem', color:'#9ca3af', marginTop:1 }}>{s.assessments_done} tests</div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Tabs */}
          <div style={{ display:'flex', gap:4, background:'#f3f4f6', borderRadius:10, padding:4, width:'fit-content', marginBottom:20 }}>
            {[['overview','Overview'],['trends','Trends'],['detail','All Marks']].map(([k,l]) => (
              <button key={k} onClick={() => setActiveTab(k)} className="sms-btn"
                style={{ background:activeTab===k?'#fff':'transparent', boxShadow:activeTab===k?'0 1px 4px rgba(0,0,0,0.1)':'none',
                  color:activeTab===k?'#111':'#6b7280', padding:'7px 16px', borderRadius:8 }}>{l}</button>
            ))}
          </div>

          {/* OVERVIEW tab */}
          {activeTab === 'overview' && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
              {/* Me vs Class chart */}
              <div className="sms-card">
                <h5 style={{ fontWeight:700, marginBottom:16 }}>Me vs Class Average</h5>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={subjectBarData} layout="vertical" barGap={3}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                    <XAxis type="number" domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{fontSize:10}} />
                    <YAxis type="category" dataKey="name" tick={{fontSize:10}} width={80} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize:'0.75rem' }} />
                    <Bar dataKey="mine"  name="My Score"     radius={[0,4,4,0]} fill={primary} />
                    <Bar dataKey="class" name="Class Avg"    radius={[0,4,4,0]} fill="#e5e7eb" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Subject detail list */}
              <div className="sms-card">
                <h5 style={{ fontWeight:700, marginBottom:14 }}>Subject Breakdown</h5>
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {subjects.map(s => {
                    const avg = parseFloat(s.my_avg);
                    const col = gradeColor(avg);
                    const diff = s.class_avg ? (avg - parseFloat(s.class_avg)).toFixed(1) : null;
                    return (
                      <div key={s.subject_id} style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:600, fontSize:'0.85rem' }}>{s.subject_name}</div>
                          {s.teacher_first_name && (
                            <div style={{ fontSize:'0.72rem', color:'#9ca3af' }}>
                              {s.teacher_first_name} {s.teacher_surname}
                            </div>
                          )}
                        </div>
                        <div style={{ width:80, height:6, background:'#f3f4f6', borderRadius:3, overflow:'hidden' }}>
                          <div style={{ width:`${avg}%`, height:'100%', background:col, borderRadius:3, transition:'width 0.5s' }} />
                        </div>
                        <div style={{ minWidth:36, textAlign:'right', fontWeight:700, color:col, fontSize:'0.88rem' }}>{avg}%</div>
                        <div style={{ minWidth:24, textAlign:'center', fontWeight:800, fontSize:'0.95rem', color:col }}>
                          {gradeSymbol(avg)}
                        </div>
                        {diff !== null && (
                          <div style={{ minWidth:40, textAlign:'right', fontSize:'0.72rem',
                            color: parseFloat(diff) >= 0 ? '#10b981' : '#ef4444', fontWeight:600 }}>
                            {parseFloat(diff) >= 0 ? `+${diff}` : diff}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* TRENDS tab */}
          {activeTab === 'trends' && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
              {/* Overall trend */}
              <div className="sms-card">
                <h5 style={{ fontWeight:700, marginBottom:16 }}>Performance Trend (Last 12 months)</h5>
                {trendData.length > 1 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="month" tick={{ fontSize:10 }} />
                      <YAxis domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{ fontSize:10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <ReferenceLine y={75} stroke="#10b981" strokeDasharray="4 2" label={{ value:'75%', fontSize:9, fill:'#10b981' }} />
                      <ReferenceLine y={50} stroke="#ef4444" strokeDasharray="4 2" label={{ value:'50%', fontSize:9, fill:'#ef4444' }} />
                      <Line type="monotone" dataKey="avg" stroke={primary} strokeWidth={2.5}
                        dot={{ r:4, fill:primary }} name="My Average" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <p style={{ color:'#9ca3af', textAlign:'center', paddingTop:60 }}>Need more data for trend</p>}
              </div>

              {/* Per-subject trend (assessment timeline) */}
              <div className="sms-card">
                <h5 style={{ fontWeight:700, marginBottom:16 }}>Assessment Timeline</h5>
                {filteredAssessments.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={filteredAssessments.map(a => ({
                      name: a.assessment_name?.slice(0,10),
                      mine: parseFloat(a.pct),
                      class: parseFloat(a.class_avg_for_assessment) || null,
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="name" tick={{ fontSize:9 }} angle={-20} textAnchor="end" height={36} />
                      <YAxis domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{ fontSize:10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Line type="monotone" dataKey="mine"  stroke={primary}    strokeWidth={2.5} dot={{ r:4 }} name="My Score" />
                      <Line type="monotone" dataKey="class" stroke="#d1d5db" strokeWidth={1.5} strokeDasharray="4 2" dot={false} name="Class Avg" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <p style={{ color:'#9ca3af', textAlign:'center', paddingTop:60 }}>No data</p>}
              </div>
            </div>
          )}

          {/* DETAIL tab */}
          {activeTab === 'detail' && (
            <div className="sms-card" style={{ padding:0, overflow:'hidden' }}>
              <table className="sms-table">
                <thead>
                  <tr>
                    <th>Date</th><th>Subject</th><th>Assessment</th><th>Type</th>
                    <th>Score</th><th>Total</th><th>My %</th><th>Class Avg</th><th>Symbol</th><th>Comment</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAssessments.map((a, i) => {
                    const col  = gradeColor(parseFloat(a.pct));
                    const diff = a.class_avg_for_assessment
                      ? (parseFloat(a.pct) - parseFloat(a.class_avg_for_assessment)).toFixed(1)
                      : null;
                    return (
                      <tr key={i}>
                        <td style={{ color:'#6b7280', fontSize:'0.8rem', whiteSpace:'nowrap' }}>{a.date || '—'}</td>
                        <td style={{ fontWeight:600, fontSize:'0.85rem' }}>{a.subject_name}</td>
                        <td style={{ fontSize:'0.85rem' }}>{a.assessment_name}</td>
                        <td style={{ textTransform:'capitalize', fontSize:'0.8rem', color:'#6b7280' }}>{a.type}</td>
                        <td style={{ fontWeight:700, color:col }}>{a.score}</td>
                        <td style={{ color:'#6b7280' }}>{a.total_marks}</td>
                        <td>
                          <span style={{ fontWeight:700, color:col }}>{a.pct}%</span>
                        </td>
                        <td style={{ color:'#6b7280', fontSize:'0.82rem' }}>
                          {a.class_avg_for_assessment ? `${a.class_avg_for_assessment}%` : '—'}
                          {diff !== null && (
                            <span style={{ marginLeft:4, fontSize:'0.72rem',
                              color: parseFloat(diff) >= 0 ? '#10b981' : '#ef4444', fontWeight:600 }}>
                              ({parseFloat(diff) >= 0 ? `+${diff}` : diff})
                            </span>
                          )}
                        </td>
                        <td style={{ fontWeight:800, color:col, fontSize:'1rem' }}>{gradeSymbol(parseFloat(a.pct))}</td>
                        <td style={{ color:'#6b7280', fontSize:'0.78rem', maxWidth:150,
                          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {a.comment || '—'}
                        </td>
                      </tr>
                    );
                  })}
                  {filteredAssessments.length === 0 && (
                    <tr><td colSpan="10" style={{ textAlign:'center', color:'#9ca3af', padding:'40px 0' }}>No assessments found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
